import { useState, useEffect } from 'react';
import { useApp } from '../../context/AppContext';
import {
    X,
    Shield,
    Lock,
    CheckCircle2,
    UserCircle,
    Bell,
    ChevronRight,
    LogOut,
    Mail,
    User,
    KeyRound,
    Keyboard,
    MessageSquare,
    RefreshCw,
    Info,
    Download
} from 'lucide-react';

export default function SettingsModal() {
    const {
        showSettings,
        setShowSettings,
        currentUser,
        updateUser,
        requestNotificationPermission,
        logout,
        isAdmin,
        settings
    } = useApp();

    const [activeTab, setActiveTab] = useState('profile');
    const [isEditingProfile, setIsEditingProfile] = useState(false);
    const [formData, setFormData] = useState({
        name: currentUser?.name || '',
        email: currentUser?.email || '',
        password: '',
        confirmPassword: ''
    });

    // Send delay preference state (local before save)
    const currentDelay = currentUser?.preferences?.send_delay;
    const [sendDelayEnabled, setSendDelayEnabled] = useState(currentDelay !== null && currentDelay !== undefined);
    const [sendDelaySeconds, setSendDelaySeconds] = useState(typeof currentDelay === 'number' ? currentDelay : 3);
    const [savingDelay, setSavingDelay] = useState(false);
    const [showSaveSuccess, setShowSaveSuccess] = useState(false);

    // Unread email notification preference
    const [unreadEmailEnabled, setUnreadEmailEnabled] = useState(
        !!currentUser?.preferences?.unread_email_enabled
    );
    const [unreadEmailDelay, setUnreadEmailDelay] = useState<number>(
        currentUser?.preferences?.unread_email_delay_minutes || 30
    );
    const [savingUnreadEmail, setSavingUnreadEmail] = useState(false);
    const [unreadEmailSaveSuccess, setUnreadEmailSaveSuccess] = useState(false);
    const [appVersion, setAppVersion] = useState<string>('1.0.0');
    const [updateStatus, setUpdateStatus] = useState<{ type: 'idle' | 'checking' | 'available' | 'uptodate' | 'error' | 'downloaded', message?: string }>({ type: 'idle' });
    const isDesktop = !!(window as any).electronAPI;

    useEffect(() => {
        if (isDesktop && showSettings) {
            (window as any).electronAPI.getVersion().then(setAppVersion);

            const unsubError = (window as any).electronAPI.onUpdateError?.((err: string) => {
                setUpdateStatus({ type: 'error', message: err });
            });
            const unsubAvailable = (window as any).electronAPI.onUpdateAvailable?.((info: any) => {
                setUpdateStatus({ type: 'available', message: `Version ${info.version} is available!` });
            });
            const unsubNotAvailable = (window as any).electronAPI.onUpdateNotAvailable?.(() => {
                setUpdateStatus({ type: 'uptodate' });
            });
            const unsubDownloaded = (window as any).electronAPI.onUpdateDownloaded?.((info: any) => {
                setUpdateStatus({ type: 'downloaded', message: `Version ${info.version} downloaded and ready.` });
            });

            return () => {
                unsubError?.();
                unsubAvailable?.();
                unsubNotAvailable?.();
                unsubDownloaded?.();
            };
        }
    }, [isDesktop, showSettings]);

    const handleUpdateProfile = async () => {
        if (!currentUser) return;

        if (formData.password && formData.password !== formData.confirmPassword) {
            alert("Passwords do not match");
            return;
        }

        try {
            const updates: any = { name: formData.name };
            if (formData.password) {
                updates.password = formData.password;
            }

            await updateUser(currentUser.id, updates);
            setIsEditingProfile(false);
            setFormData(prev => ({ ...prev, password: '', confirmPassword: '' }));
            // Maybe show success toast?
        } catch (error) {
            console.error('Failed to update profile:', error);
            alert('Failed to update profile');
        }
    };

    const handleClose = () => {
        setShowSettings(false);
    };

    useEffect(() => {
        const handleEsc = (event: KeyboardEvent) => {
            if (event.key === 'Escape') handleClose();
        };
        if (showSettings) {
            document.addEventListener('keydown', handleEsc);
        }
        return () => document.removeEventListener('keydown', handleEsc);
    }, [showSettings]);

    if (!showSettings) return null;

    const tabs = [
        { id: 'profile', label: 'Profile', icon: UserCircle },
        { id: 'messaging', label: 'Messaging', icon: MessageSquare },
        { id: 'shortcuts', label: 'Keyboard Shortcuts', icon: Keyboard },
        { id: 'security', label: 'Privacy & Security', icon: Shield },
        { id: 'notifications', label: 'Notifications', icon: Bell },
        ...(isDesktop ? [{ id: 'updates', label: 'App Updates', icon: Download }] : [])
    ];

    return (
        <div className="modal-overlay" onClick={handleClose} style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            backdropFilter: 'blur(4px)'
        }}>
            <div
                className="modal"
                onClick={e => e.stopPropagation()}
                style={{
                    width: '100%',
                    maxWidth: 850,
                    height: 600,
                    background: 'var(--bg-primary)',
                    borderRadius: 16,
                    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    border: '1px solid var(--border-color)',
                    animation: 'modalSlideIn 0.2s ease-out'
                }}
            >
                {/* Header */}
                <div style={{
                    padding: '20px 24px',
                    borderBottom: '1px solid var(--border-color)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    background: 'var(--bg-primary)'
                }}>
                    <h2 style={{ fontSize: 20, fontWeight: 600, margin: 0 }}>Settings</h2>
                    <button
                        onClick={handleClose}
                        style={{
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            color: 'var(--text-secondary)',
                            padding: 8,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'background 0.2s'
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-secondary)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >
                        <X size={20} strokeWidth={2} />
                    </button>
                </div>

                <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
                    {/* Sidebar */}
                    <div style={{
                        width: 240,
                        borderRight: '1px solid var(--border-color)',
                        background: 'var(--bg-secondary)',
                        padding: 16,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 4
                    }}>
                        {tabs.map(tab => {
                            const isActive = activeTab === tab.id;
                            const Icon = tab.icon;
                            return (
                                <button
                                    key={tab.id}
                                    onClick={() => setActiveTab(tab.id)}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 12,
                                        width: '100%',
                                        padding: '10px 12px',
                                        border: 'none',
                                        borderRadius: 8,
                                        background: isActive ? 'var(--bg-primary)' : 'transparent',
                                        color: isActive ? 'var(--text-primary)' : 'var(--text-secondary)',
                                        fontWeight: isActive ? 600 : 500,
                                        cursor: 'pointer',
                                        transition: 'all 0.2s',
                                        boxShadow: isActive ? '0 1px 2px rgba(0,0,0,0.05)' : 'none',
                                        fontSize: 14
                                    }}
                                >
                                    <Icon size={18} strokeWidth={2} color={isActive ? '#6366f1' : 'currentColor'} />
                                    {tab.label}
                                    {isActive && <ChevronRight size={14} style={{ marginLeft: 'auto', opacity: 0.5 }} />}
                                </button>
                            );
                        })}

                        <div style={{ marginTop: 'auto', paddingTop: 16, borderTop: '1px solid var(--border-color)' }}>
                            <button
                                onClick={() => { logout(); handleClose(); }}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 12,
                                    width: '100%',
                                    padding: '10px 12px',
                                    border: 'none',
                                    borderRadius: 8,
                                    background: 'rgba(239, 68, 68, 0.05)',
                                    color: '#ef4444',
                                    fontWeight: 500,
                                    cursor: 'pointer',
                                    fontSize: 14,
                                    transition: 'background 0.2s'
                                }}
                                onMouseEnter={e => e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)'}
                                onMouseLeave={e => e.currentTarget.style.background = 'rgba(239, 68, 68, 0.05)'}
                            >
                                <LogOut size={18} strokeWidth={2} />
                                Sign Out
                            </button>
                        </div>
                    </div>

                    {/* Content Area */}
                    <div style={{ flex: 1, overflowY: 'auto', padding: '32px 40px', background: 'var(--bg-primary)' }}>
                        {activeTab === 'profile' && (
                            <div style={{ maxWidth: 500 }}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 32 }}>
                                    <h3 style={{ fontSize: 24, fontWeight: 700, margin: 0 }}>My Profile</h3>
                                    {!isEditingProfile && (settings?.allow_profile_edit !== false || isAdmin) && (
                                        <button
                                            className="btn btn-secondary"
                                            onClick={() => setIsEditingProfile(true)}
                                            style={{ fontSize: 13 }}
                                        >
                                            Edit Profile
                                        </button>
                                    )}
                                </div>

                                <div style={{ display: 'flex', alignItems: 'center', gap: 24, marginBottom: 40 }}>
                                    <div style={{
                                        width: 80,
                                        height: 80,
                                        borderRadius: '50%',
                                        background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: 32,
                                        color: 'white',
                                        fontWeight: 700,
                                        boxShadow: '0 10px 20px -5px rgba(99, 102, 241, 0.4)'
                                    }}>
                                        {currentUser?.name.charAt(0)}
                                    </div>
                                    <div>
                                        <h4 style={{ fontSize: 20, fontWeight: 600, margin: '0 0 4px 0' }}>{currentUser?.name}</h4>
                                        <p style={{ color: 'var(--text-secondary)', margin: 0, fontSize: 14 }}>{currentUser?.email}</p>
                                        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                                            <span style={{
                                                fontSize: 11,
                                                fontWeight: 600,
                                                padding: '2px 8px',
                                                borderRadius: 20,
                                                background: 'var(--bg-secondary)',
                                                color: 'var(--text-secondary)',
                                                border: '1px solid var(--border-color)',
                                                textTransform: 'uppercase'
                                            }}>
                                                {currentUser?.role?.name || 'User'}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {isEditingProfile ? (
                                    <form onSubmit={(e) => { e.preventDefault(); handleUpdateProfile(); }} style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                                        <div>
                                            <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 8, color: 'var(--text-secondary)' }}>Full Name</label>
                                            <div style={{ position: 'relative' }}>
                                                <User size={16} style={{ position: 'absolute', left: 12, top: 12, color: 'var(--text-muted)' }} />
                                                <input
                                                    type="text"
                                                    value={formData.name}
                                                    onChange={e => setFormData({ ...formData, name: e.target.value })}
                                                    required
                                                    className="input"
                                                    style={{ paddingLeft: 36, width: '100%' }}
                                                />
                                            </div>
                                        </div>

                                        <div>
                                            <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 8, color: 'var(--text-secondary)' }}>Email Address</label>
                                            <div style={{ position: 'relative' }}>
                                                <Mail size={16} style={{ position: 'absolute', left: 12, top: 12, color: 'var(--text-muted)' }} />
                                                <input
                                                    type="email"
                                                    value={formData.email}
                                                    disabled
                                                    className="input"
                                                    style={{ paddingLeft: 36, width: '100%', cursor: 'not-allowed', opacity: 0.7 }}
                                                />
                                            </div>
                                            <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6 }}>Email addresses are managed by your administrator.</p>
                                        </div>

                                        <div style={{ borderTop: '1px solid var(--border-color)', paddingTop: 20, marginTop: 10 }}>
                                            <h5 style={{ fontSize: 15, fontWeight: 600, marginBottom: 16 }}>Change Password</h5>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                                                <div>
                                                    <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 8, color: 'var(--text-secondary)' }}>New Password</label>
                                                    <div style={{ position: 'relative' }}>
                                                        <KeyRound size={16} style={{ position: 'absolute', left: 12, top: 12, color: 'var(--text-muted)' }} />
                                                        <input
                                                            type="password"
                                                            value={formData.password}
                                                            onChange={e => setFormData({ ...formData, password: e.target.value })}
                                                            placeholder="Leave blank to keep current"
                                                            className="input"
                                                            style={{ paddingLeft: 36, width: '100%' }}
                                                        />
                                                    </div>
                                                </div>
                                                {formData.password && (
                                                    <div>
                                                        <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 8, color: 'var(--text-secondary)' }}>Confirm Password</label>
                                                        <div style={{ position: 'relative' }}>
                                                            <KeyRound size={16} style={{ position: 'absolute', left: 12, top: 12, color: 'var(--text-muted)' }} />
                                                            <input
                                                                type="password"
                                                                value={formData.confirmPassword}
                                                                onChange={e => setFormData({ ...formData, confirmPassword: e.target.value })}
                                                                placeholder="Confirm new password"
                                                                className="input"
                                                                style={{ paddingLeft: 36, width: '100%' }}
                                                            />
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        <div style={{ display: 'flex', gap: 12, marginTop: 20 }}>
                                            <button
                                                type="button"
                                                className="btn btn-ghost"
                                                onClick={() => {
                                                    setIsEditingProfile(false);
                                                    setFormData({
                                                        name: currentUser?.name || '',
                                                        email: currentUser?.email || '',
                                                        password: '',
                                                        confirmPassword: ''
                                                    });
                                                }}
                                                style={{ flex: 1 }}
                                            >
                                                Cancel
                                            </button>
                                            <button type="submit" className="btn btn-primary" style={{ flex: 1 }}>
                                                Save Changes
                                            </button>
                                        </div>
                                    </form>
                                ) : null}
                            </div>
                        )}

                        {activeTab === 'messaging' && (
                            <div style={{ maxWidth: 500 }}>
                                <h3 style={{ fontSize: 24, fontWeight: 700, margin: '0 0 32px 0' }}>Messaging</h3>

                                <div style={{ border: '1px solid var(--border-color)', borderRadius: 12, overflow: 'hidden' }}>
                                    <div style={{ padding: 24 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                                            <div>
                                                <h4 style={{ fontSize: 16, fontWeight: 600, margin: '0 0 4px 0' }}>Send Delay</h4>
                                                <p style={{ fontSize: 14, color: 'var(--text-secondary)', margin: 0 }}>
                                                    Adds a brief countdown before your message is sent, giving you time to undo.
                                                </p>
                                            </div>
                                            {/* Toggle */}
                                            <button
                                                type="button"
                                                onClick={() => setSendDelayEnabled(e => !e)}
                                                style={{
                                                    width: 44, height: 24,
                                                    borderRadius: 12, border: 'none', cursor: 'pointer',
                                                    background: sendDelayEnabled ? 'var(--accent-primary)' : 'var(--bg-tertiary)',
                                                    position: 'relative', transition: 'background 0.2s', flexShrink: 0, marginLeft: 16
                                                }}
                                                title={sendDelayEnabled ? 'Disable send delay' : 'Enable send delay'}
                                            >
                                                <span style={{
                                                    position: 'absolute', top: 2,
                                                    left: sendDelayEnabled ? 22 : 2,
                                                    width: 20, height: 20,
                                                    borderRadius: '50%', background: 'white',
                                                    transition: 'left 0.2s',
                                                    boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                                                }} />
                                            </button>
                                        </div>

                                        {sendDelayEnabled && (
                                            <div style={{ marginTop: 20, paddingTop: 20, borderTop: '1px solid var(--border-color)' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                                                    <label style={{ fontSize: 14, fontWeight: 500 }}>Delay duration</label>
                                                    <span style={{
                                                        fontSize: 15, fontWeight: 700,
                                                        color: 'var(--accent-primary)',
                                                        background: 'rgba(99,102,241,0.1)',
                                                        padding: '3px 10px', borderRadius: 20
                                                    }}>{sendDelaySeconds}s</span>
                                                </div>
                                                <input
                                                    type="range"
                                                    min={1} max={5} step={1}
                                                    value={sendDelaySeconds}
                                                    onChange={e => setSendDelaySeconds(Number(e.target.value))}
                                                    style={{ width: '100%', accentColor: 'var(--accent-primary)' }}
                                                />
                                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                                                    {[1, 2, 3, 4, 5].map(s => <span key={s}>{s}s</span>)}
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    <div style={{ padding: '12px 24px', background: 'var(--bg-secondary)', display: 'flex', justifyContent: 'flex-end' }}>
                                        <button
                                            className={`btn ${showSaveSuccess ? 'btn-success' : 'btn-primary'}`}
                                            disabled={savingDelay}
                                            onClick={async () => {
                                                if (!currentUser) return;
                                                setSavingDelay(true);
                                                try {
                                                    await updateUser(currentUser.id, {
                                                        preferences: {
                                                            ...currentUser.preferences,
                                                            send_delay: sendDelayEnabled ? sendDelaySeconds : null
                                                        }
                                                    });
                                                    setShowSaveSuccess(true);
                                                    setTimeout(() => setShowSaveSuccess(false), 3000);
                                                } finally {
                                                    setSavingDelay(false);
                                                }
                                            }}
                                            style={{
                                                fontSize: 13,
                                                minWidth: 80,
                                                backgroundColor: showSaveSuccess ? '#10b981' : undefined,
                                                borderColor: showSaveSuccess ? '#10b981' : undefined,
                                                color: showSaveSuccess ? 'white' : undefined,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: 4
                                            }}
                                        >
                                            {savingDelay ? (
                                                <>
                                                    <RefreshCw size={14} className="animate-spin" style={{ animation: 'spin 1.s linear infinite' }} />
                                                    Saving…
                                                </>
                                            ) : showSaveSuccess ? (
                                                <>
                                                    <CheckCircle2 size={14} />
                                                    Saved!
                                                </>
                                            ) : 'Save'}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {activeTab === 'security' && (
                            <div style={{ maxWidth: 500 }}>
                                <h3 style={{ fontSize: 24, fontWeight: 700, margin: '0 0 32px 0' }}>Privacy & Security</h3>

                                <div style={{
                                    padding: 24,
                                    background: 'rgba(16, 185, 129, 0.05)',
                                    border: '1px solid rgba(16, 185, 129, 0.2)',
                                    borderRadius: 12,
                                    marginBottom: 32
                                }}>
                                    <div style={{ display: 'flex', gap: 16 }}>
                                        <div style={{
                                            width: 40, height: 40, borderRadius: '50%',
                                            background: '#10b981', color: 'white',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            flexShrink: 0
                                        }}>
                                            <Lock size={20} />
                                        </div>
                                        <div>
                                            <h4 style={{ fontSize: 16, fontWeight: 600, color: '#059669', margin: '0 0 4px 0' }}>
                                                End-to-End Encryption
                                            </h4>
                                            <p style={{ fontSize: 14, color: '#047857', margin: 0, lineHeight: 1.5 }}>
                                                Your messages are secured with military-grade encryption. Only you and the intended recipients can read them. Not even the server can see your private conversations.
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 16, borderRadius: 12, border: '1px solid var(--border-color)', background: 'var(--bg-secondary)' }}>
                                        <CheckCircle2 size={20} color="#10b981" />
                                        <div>
                                            <h5 style={{ fontSize: 14, fontWeight: 600, margin: 0 }}>Encryption Active</h5>
                                            <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: 0 }}>All messaging features are secure.</p>
                                        </div>
                                    </div>

                                    {currentUser?.enc_private_key && (
                                        <div style={{ padding: 16, borderRadius: 12, border: '1px solid var(--border-color)', background: 'var(--bg-secondary)' }}>
                                            <h5 style={{ fontSize: 14, fontWeight: 600, margin: '0 0 8px 0', display: 'flex', alignItems: 'center', gap: 8 }}>
                                                <KeyRound size={16} />
                                                Key Backup
                                            </h5>
                                            <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: '0 0 16px 0' }}>
                                                Export your encrypted private key for manual backup. You will need your password to restore it.
                                            </p>
                                            <button
                                                className="btn btn-secondary"
                                                onClick={() => {
                                                    const backupData = JSON.stringify({
                                                        enc_private_key: currentUser.enc_private_key,
                                                        key_salt: currentUser.key_salt,
                                                        email: currentUser.email
                                                    }, null, 2);
                                                    navigator.clipboard.writeText(backupData);
                                                    alert('Encrypted key backup copied to clipboard!');
                                                }}
                                                style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
                                            >
                                                <LogOut size={16} />
                                                Copy Encrypted Key Backup
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {activeTab === 'notifications' && (
                            <div style={{ maxWidth: 500 }}>
                                <h3 style={{ fontSize: 24, fontWeight: 700, margin: '0 0 32px 0' }}>Notifications</h3>

                                {/* Desktop / Browser Push Notifications */}
                                <div style={{ border: '1px solid var(--border-color)', borderRadius: 12, overflow: 'hidden', marginBottom: 24 }}>
                                    <div style={{ padding: 24, borderBottom: '1px solid var(--border-color)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <div>
                                            <h4 style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>Desktop Notifications</h4>
                                            <p style={{ fontSize: 14, color: 'var(--text-secondary)', margin: 0 }}>
                                                Get notified when you receive new messages while the app is running.
                                            </p>
                                            {typeof Notification !== 'undefined' && Notification.permission === 'granted' && (
                                                <p style={{ fontSize: 12, color: '#2eb67d', margin: '4px 0 0 0' }}>✓ Browser notifications are enabled</p>
                                            )}
                                            {typeof Notification !== 'undefined' && Notification.permission === 'denied' && (
                                                <p style={{ fontSize: 12, color: 'var(--accent-danger)', margin: '4px 0 0 0' }}>⚠ Notifications are blocked — enable in browser settings</p>
                                            )}
                                        </div>
                                        {typeof Notification !== 'undefined' && Notification.permission !== 'granted' ? (
                                            <button
                                                className="btn btn-secondary"
                                                onClick={async () => {
                                                    if (Notification.permission === 'denied') {
                                                        alert('Notifications are blocked. Please enable them in your browser or OS settings.');
                                                        return;
                                                    }
                                                    const granted = await requestNotificationPermission();
                                                    if (granted) alert('Notifications enabled!');
                                                    else alert('Permission denied. Please enable in browser settings.');
                                                }}
                                            >
                                                {Notification.permission === 'denied' ? 'Blocked (change in settings)' : 'Enable'}
                                            </button>
                                        ) : (
                                            <button className="btn btn-secondary" disabled style={{ opacity: 0.7, cursor: 'default', color: '#2eb67d', borderColor: '#2eb67d' }}>
                                                Enabled ✓
                                            </button>
                                        )}
                                    </div>
                                    <div style={{ padding: 16, background: 'var(--bg-secondary)', display: 'flex', gap: 12, alignItems: 'center' }}>
                                        <Bell size={16} color="var(--text-muted)" />
                                        <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                                            Note: You may also need to allow notifications in your browser settings.
                                        </span>
                                    </div>
                                </div>

                                {/* Unread Message Email Digest — only shown when admin enables the feature */}
                                {settings?.enable_unread_email_notifications && (
                                    <div style={{ border: '1px solid var(--border-color)', borderRadius: 12, overflow: 'hidden' }}>
                                        <div style={{ padding: 24 }}>
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                                                <div>
                                                    <h4 style={{ fontSize: 16, fontWeight: 600, margin: '0 0 4px 0', display: 'flex', alignItems: 'center', gap: 8 }}>
                                                        <Mail size={16} strokeWidth={2} />
                                                        Unread Message Emails
                                                    </h4>
                                                    <p style={{ fontSize: 14, color: 'var(--text-secondary)', margin: 0 }}>
                                                        Receive a digest email when messages go unread past the delay you set.
                                                    </p>
                                                </div>
                                                {/* Toggle */}
                                                <button
                                                    id="unread-email-toggle"
                                                    type="button"
                                                    onClick={() => setUnreadEmailEnabled(e => !e)}
                                                    style={{
                                                        width: 44, height: 24,
                                                        borderRadius: 12, border: 'none', cursor: 'pointer',
                                                        background: unreadEmailEnabled ? 'var(--accent-primary)' : 'var(--bg-tertiary)',
                                                        position: 'relative', transition: 'background 0.2s', flexShrink: 0, marginLeft: 16
                                                    }}
                                                    title={unreadEmailEnabled ? 'Disable unread email notifications' : 'Enable unread email notifications'}
                                                >
                                                    <span style={{
                                                        position: 'absolute', top: 2,
                                                        left: unreadEmailEnabled ? 22 : 2,
                                                        width: 20, height: 20,
                                                        borderRadius: '50%', background: 'white',
                                                        transition: 'left 0.2s',
                                                        boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                                                    }} />
                                                </button>
                                            </div>

                                            {unreadEmailEnabled && (
                                                <div style={{ marginTop: 20, paddingTop: 20, borderTop: '1px solid var(--border-color)' }}>
                                                    <label style={{ fontSize: 14, fontWeight: 500, display: 'block', marginBottom: 10 }}>
                                                        Send email after messages are unread for:
                                                    </label>
                                                    <select
                                                        id="unread-email-delay-select"
                                                        className="input"
                                                        value={unreadEmailDelay}
                                                        onChange={e => setUnreadEmailDelay(Number(e.target.value))}
                                                        style={{ width: '100%', maxWidth: 260 }}
                                                    >
                                                        <option value={15}>15 minutes</option>
                                                        <option value={30}>30 minutes</option>
                                                        <option value={60}>1 hour</option>
                                                        <option value={120}>2 hours</option>
                                                        <option value={240}>4 hours</option>
                                                        <option value={480}>8 hours</option>
                                                    </select>
                                                    <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 8 }}>
                                                        Multiple unread messages are grouped into a single email with links
                                                        to jump directly to each conversation. Emails are sent to <strong>{currentUser?.email}</strong>.
                                                    </p>
                                                </div>
                                            )}
                                        </div>

                                        <div style={{ padding: '12px 24px', background: 'var(--bg-secondary)', display: 'flex', justifyContent: 'flex-end' }}>
                                            <button
                                                id="unread-email-save-btn"
                                                className="btn btn-primary"
                                                disabled={savingUnreadEmail}
                                                onClick={async () => {
                                                    if (!currentUser) return;
                                                    setSavingUnreadEmail(true);
                                                    try {
                                                        await updateUser(currentUser.id, {
                                                            preferences: {
                                                                ...currentUser.preferences,
                                                                unread_email_enabled: unreadEmailEnabled,
                                                                unread_email_delay_minutes: unreadEmailDelay
                                                            }
                                                        });
                                                        setUnreadEmailSaveSuccess(true);
                                                        setTimeout(() => setUnreadEmailSaveSuccess(false), 3000);
                                                    } finally {
                                                        setSavingUnreadEmail(false);
                                                    }
                                                }}
                                                style={{
                                                    fontSize: 13,
                                                    minWidth: 80,
                                                    backgroundColor: unreadEmailSaveSuccess ? '#10b981' : undefined,
                                                    borderColor: unreadEmailSaveSuccess ? '#10b981' : undefined,
                                                    color: unreadEmailSaveSuccess ? 'white' : undefined,
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4
                                                }}
                                            >
                                                {savingUnreadEmail ? (
                                                    <><RefreshCw size={14} style={{ animation: 'spin 1s linear infinite' }} /> Saving…</>
                                                ) : unreadEmailSaveSuccess ? (
                                                    <><CheckCircle2 size={14} /> Saved!</>
                                                ) : 'Save'}
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {activeTab === 'shortcuts' && (
                            <div style={{ maxWidth: 500 }}>
                                <h3 style={{ fontSize: 24, fontWeight: 700, margin: '0 0 32px 0' }}>Keyboard Shortcuts</h3>

                                <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                                    <section>
                                        <h4 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
                                            <Keyboard size={18} color="#6366f1" />
                                            Messaging
                                        </h4>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: '1px solid var(--border-color)' }}>
                                                <span style={{ fontSize: 14 }}>Send message</span>
                                                <kbd style={{ background: 'var(--bg-secondary)', padding: '2px 6px', borderRadius: 4, border: '1px solid var(--border-color)', fontSize: 12, fontFamily: 'monospace' }}>Enter</kbd>
                                            </div>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: '1px solid var(--border-color)' }}>
                                                <span style={{ fontSize: 14 }}>Add new line</span>
                                                <kbd style={{ background: 'var(--bg-secondary)', padding: '2px 6px', borderRadius: 4, border: '1px solid var(--border-color)', fontSize: 12, fontFamily: 'monospace' }}>Shift + Enter</kbd>
                                            </div>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: '1px solid var(--border-color)' }}>
                                                <span style={{ fontSize: 14 }}>Edit last message</span>
                                                <kbd style={{ background: 'var(--bg-secondary)', padding: '2px 6px', borderRadius: 4, border: '1px solid var(--border-color)', fontSize: 12, fontFamily: 'monospace' }}>↑ (Up Arrow)</kbd>
                                            </div>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: '1px solid var(--border-color)' }}>
                                                <span style={{ fontSize: 14 }}>Mention someone</span>
                                                <kbd style={{ background: 'var(--bg-secondary)', padding: '2px 6px', borderRadius: 4, border: '1px solid var(--border-color)', fontSize: 12, fontFamily: 'monospace' }}>@</kbd>
                                            </div>
                                        </div>
                                    </section>
                                </div>
                            </div>
                        )}

                        {activeTab === 'updates' && (
                            <div style={{ maxWidth: 500 }}>
                                <h3 style={{ fontSize: 24, fontWeight: 700, margin: '0 0 32px 0' }}>App Updates</h3>

                                <div style={{
                                    padding: 24,
                                    borderRadius: 16,
                                    background: 'var(--bg-secondary)',
                                    border: '1px solid var(--border-color)',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    textAlign: 'center',
                                    gap: 16
                                }}>
                                    <div style={{
                                        width: 64, height: 64, borderRadius: 20,
                                        background: 'var(--bg-primary)',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                                        border: '1px solid var(--border-color)',
                                        marginBottom: 8
                                    }}>
                                        <Download size={32} color="var(--accent-primary)" />
                                    </div>

                                    <div>
                                        <h4 style={{ fontSize: 18, fontWeight: 600, margin: '0 0 4px 0' }}>Tock Desktop</h4>
                                        <p style={{ fontSize: 14, color: 'var(--text-secondary)', margin: 0 }}>
                                            Current Version: <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>v{appVersion}</span>
                                        </p>
                                    </div>

                                    <div style={{ width: '100%', height: 1, background: 'var(--border-color)', margin: '8px 0' }} />

                                    <div style={{ width: '100%' }}>
                                        {updateStatus.type === 'idle' && (
                                            <button
                                                className="btn btn-primary"
                                                onClick={() => {
                                                    setUpdateStatus({ type: 'checking' });
                                                    (window as any).electronAPI.checkForUpdates();
                                                }}
                                                style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
                                            >
                                                <RefreshCw size={16} />
                                                Check for Updates
                                            </button>
                                        )}

                                        {updateStatus.type === 'checking' && (
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, color: 'var(--text-secondary)', padding: 12 }}>
                                                <RefreshCw size={18} className="animate-spin" style={{ animation: 'spin 1s linear infinite' }} />
                                                <span style={{ fontSize: 14 }}>Checking for updates...</span>
                                            </div>
                                        )}

                                        {updateStatus.type === 'uptodate' && (
                                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
                                                <div style={{ color: '#10b981', display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, fontWeight: 600 }}>
                                                    <CheckCircle2 size={18} />
                                                    You're up to date!
                                                </div>
                                                <button
                                                    className="btn btn-secondary"
                                                    onClick={() => setUpdateStatus({ type: 'idle' })}
                                                    style={{ width: '100%', fontSize: 13 }}
                                                >
                                                    Check Again
                                                </button>
                                            </div>
                                        )}

                                        {updateStatus.type === 'error' && (
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                                <div style={{
                                                    padding: 16, borderRadius: 12,
                                                    background: 'rgba(239, 68, 68, 0.05)',
                                                    border: '1px solid rgba(239, 68, 68, 0.2)',
                                                    color: '#ef4444', fontSize: 13, textAlign: 'left'
                                                }}>
                                                    <div style={{ display: 'flex', gap: 8, marginBottom: 8, fontWeight: 600 }}>
                                                        <Info size={16} />
                                                        Update Failed
                                                    </div>
                                                    {updateStatus.message}
                                                </div>
                                                <button
                                                    className="btn btn-secondary"
                                                    onClick={() => setUpdateStatus({ type: 'idle' })}
                                                    style={{ width: '100%', fontSize: 13 }}
                                                >
                                                    Try Again
                                                </button>
                                            </div>
                                        )}

                                        {(updateStatus.type === 'available' || updateStatus.type === 'downloaded') && (
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                                <div style={{
                                                    padding: 16, borderRadius: 12,
                                                    background: 'rgba(99, 102, 241, 0.05)',
                                                    border: '1px solid rgba(99, 102, 241, 0.2)',
                                                    color: 'var(--accent-primary)', fontSize: 14, fontWeight: 600
                                                }}>
                                                    {updateStatus.message}
                                                </div>
                                                {updateStatus.type === 'downloaded' ? (
                                                    <button
                                                        className="btn btn-primary"
                                                        onClick={() => (window as any).electronAPI.installUpdate()}
                                                        style={{ width: '100%' }}
                                                    >
                                                        Restart to Update
                                                    </button>
                                                ) : (
                                                    <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                                                        Downloading in the background...
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div style={{ marginTop: 24, padding: 16, borderRadius: 12, border: '1px dotted var(--border-color)', display: 'flex', gap: 12 }}>
                                    <Info size={16} style={{ flexShrink: 0, marginTop: 2, color: 'var(--text-muted)' }} />
                                    <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: 0, lineHeight: 1.5 }}>
                                        Tock Desktop is automatically updated to the latest version. You can manually check here if you think you've missed an update.
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div >
    );
}
